import { useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router";
import type { ProjectDTO } from "@roomate/core";
import { keys, useArchivedProjects, useCosts, useCreate, useMe, useProjectsStatus, useSetProjectArchived, type ProjectWithSummary } from "../api.ts";
import { Action, Avatar, Page, PageHeader, Pill } from "../calm.tsx";
import { useCurrentProject } from "../project.tsx";
import { Ago, cx, Empty, ErrorNote, inputCls } from "../ui.tsx";
import { Dialog, Field } from "./Project.tsx";
import { ProjectMark } from "../components/DecisionCrumb.tsx";
import { NewProjectGithub } from "../components/ProjectGithub.tsx";

type Tone = "neutral" | "live" | "signal" | "danger";

/** What the repo badge says for a project: ready / setting up on <node> / error / managed by node config. */
export function repoBadge(p: Pick<ProjectDTO, "repoMode" | "repoState" | "repoError" | "homeNodeId">): { tone: Tone; label: string } {
  if (!p.repoMode) return { tone: "neutral", label: "managed by node config" };
  if (p.repoState === "ready") return { tone: "live", label: "ready" };
  if (p.repoState === "error") return { tone: "danger", label: `error: ${p.repoError ?? "unknown"}` };
  return { tone: "signal", label: p.homeNodeId ? `setting up on ${p.homeNodeId}` : "setting up" };
}

export function ProjectsPage() {
  const projects = useProjectsStatus();
  // Spend per project from agent runs: all time (10-year window) and the last 24 hours.
  const allTime = useCosts(3650);
  const lastDay = useCosts(1);
  const spendOf = (id: string) => ({
    totalUsd: allTime.data?.byProject.find((b) => b.projectId === id)?.costUsd ?? null,
    dayUsd: lastDay.data?.byProject.find((b) => b.projectId === id)?.costUsd ?? 0,
  });
  const isAdmin = useMe().data?.access === "admin";
  const [creating, setCreating] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  return (
    <Page wide>
      <PageHeader
        title="Projects"
        meta={<span className="text-ink-2">Every project, with what is moving in it right now.</span>}
        actions={
          isAdmin ? (
            <Action primary onClick={() => setCreating(true)} testId="new-project">
              New project
            </Action>
          ) : undefined
        }
      />
      {creating && <NewProjectDialog onClose={() => setCreating(false)} />}
      <div className="mt-6 flex gap-1" role="group" aria-label="Project filter">
        {(["active", "archived"] as const).map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setShowArchived(f === "archived")}
            aria-pressed={showArchived === (f === "archived")}
            data-testid={`projects-filter-${f}`}
            className={cx("rounded-full px-3.5 py-1.5 text-[13px]", showArchived === (f === "archived") ? "bg-ink/[0.09] text-ink" : "text-ink-3 hover:text-ink-2")}
          >
            {f === "active" ? "Active" : "Archived"}
          </button>
        ))}
      </div>
      <div className="mt-6">
        {showArchived ? (
          <ArchivedProjects isAdmin={isAdmin} />
        ) : projects.error ? (
          <ErrorNote error={projects.error} />
        ) : !projects.data ? (
          <p className="text-ink-3">Loading…</p>
        ) : projects.data.length === 0 ? (
          <Empty>No projects yet.</Empty>
        ) : (
          <ul className="space-y-4" data-testid="projects-list">
            {projects.data.map((p) => (
              <li key={p.id}>
                <ProjectRow project={p} spend={spendOf(p.id)} />
              </li>
            ))}
          </ul>
        )}
      </div>
    </Page>
  );
}

/** The "Archived" filter: projects an admin archived (`?includeArchived=1`), each with an Unarchive action for admins. */
function ArchivedProjects({ isAdmin }: { isAdmin: boolean }) {
  const archived = useArchivedProjects();
  const unarchive = useSetProjectArchived();
  const navigate = useNavigate();
  if (archived.error) return <ErrorNote error={archived.error} />;
  if (!archived.data) return <p className="text-ink-3">Loading…</p>;
  if (archived.data.length === 0) return <Empty>No archived projects.</Empty>;
  return (
    <ul className="space-y-3" data-testid="archived-projects-list">
      {archived.data.map((p) => (
        <li key={p.id} data-testid="archived-project-row" data-project-id={p.id} className="glass flex flex-wrap items-center justify-between gap-3 rounded-[22px] px-6 py-4">
          <button type="button" onClick={() => navigate(`/projects/${p.id}`)} className="flex min-w-0 items-center gap-2.5 text-left">
            <ProjectMark id={p.id} name={p.name} size={22} />
            <span className="text-[17px] font-medium tracking-[-0.01em]">{p.name}</span>
            <Pill tone="neutral">Archived</Pill>
          </button>
          {unarchive.error && unarchive.variables?.id === p.id && <ErrorNote error={unarchive.error} />}
          {isAdmin && (
            <Action disabled={unarchive.isPending && unarchive.variables?.id === p.id} testId="unarchive-project" onClick={() => unarchive.mutate({ id: p.id, archived: false })}>
              Unarchive
            </Action>
          )}
        </li>
      ))}
    </ul>
  );
}

/** Where a project's open work sits, left to right, for the stage bar on its card. */
const STAGE_BAR: { key: "done" | "review" | "verifying" | "in_progress" | "ready" | "blocked"; label: string; color: string }[] = [
  { key: "in_progress", label: "Building", color: "var(--signal)" },
  { key: "verifying", label: "Checking", color: "color-mix(in oklab, var(--signal) 55%, var(--ok))" },
  { key: "review", label: "In review", color: "var(--medium)" },
  { key: "ready", label: "Ready", color: "color-mix(in oklab, var(--ink) 35%, transparent)" },
  { key: "blocked", label: "Blocked", color: "var(--high)" },
];

function ProjectRow({ project: p, spend }: { project: ProjectWithSummary; spend: { totalUsd: number | null; dayUsd: number } }) {
  const { select } = useCurrentProject();
  const navigate = useNavigate();
  const s = p.summary;
  const badge = repoBadge(p);
  const open = () => {
    select(p.id);
    navigate(`/projects/${p.id}`);
  };
  const openWork = STAGE_BAR.reduce((n, b) => n + (s.stages[b.key] ?? 0), 0);
  const shipped = s.stages.done ?? 0;
  const live = s.activeActors.length > 0;
  return (
    <button
      type="button"
      onClick={open}
      data-testid="project-row"
      data-project-id={p.id}
      className="glass group relative w-full overflow-hidden rounded-[22px] p-6 text-left transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-ink/[0.03] md:p-7"
    >
      <span aria-hidden className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-[color:var(--signal)] opacity-[0.07] blur-3xl transition-opacity duration-500 group-hover:opacity-[0.12]" />

      <span className="relative flex flex-wrap items-start justify-between gap-4">
        <span className="min-w-0">
          <span className="flex items-center gap-2.5">
            <span className={cx("h-2 w-2 shrink-0 rounded-full", live ? "bg-[color:var(--ok)] shadow-[0_0_0_4px_color-mix(in_oklab,var(--ok)_18%,transparent)]" : "bg-ink/20")} />
            <ProjectMark id={p.id} name={p.name} size={22} />
            <span className="text-[19px] font-medium tracking-[-0.01em]">{p.name}</span>
          </span>
          {p.description && <span className="mt-1 block max-w-[60ch] truncate text-[13px] text-ink-2">{p.description}</span>}
        </span>
        <span className="flex items-center gap-3">
          <span className="text-[12px] text-ink-3">{s.lastActivityAt ? <Ago ts={s.lastActivityAt} /> : "no activity"}</span>
          <Pill tone={badge.tone} testId="project-repo-badge">
            {badge.label}
          </Pill>
        </span>
      </span>

      <span className="relative mt-6 block">
        <span className="flex items-baseline justify-between text-[12px] text-ink-2">
          <span>
            <span className="text-[15px] font-medium text-ink">{openWork}</span> open · <span className="text-ink">{shipped.toLocaleString("en-US")}</span> shipped
          </span>
        </span>
        <span className="mt-2 flex h-1.5 w-full overflow-hidden rounded-full bg-ink/[0.06]" aria-hidden>
          {STAGE_BAR.map((b) => {
            const n = s.stages[b.key] ?? 0;
            return n > 0 ? <span key={b.key} style={{ width: `${(n / Math.max(1, openWork)) * 100}%`, background: b.color }} className="h-full" /> : null;
          })}
        </span>
        <span className="mt-2.5 flex flex-wrap gap-x-4 gap-y-1 text-[12px] text-ink-2" data-testid="project-stages">
          {STAGE_BAR.map((b) => (
            <span key={b.key} className="inline-flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: b.color }} />
              {b.label} <span className="text-ink">{s.stages[b.key] ?? 0}</span>
            </span>
          ))}
        </span>
      </span>

      <span className="relative mt-6 grid grid-cols-2 gap-2.5 md:grid-cols-4">
        <Metric label="Working now" testId="project-agents" value={String(s.activeActors.length)}>
          {live && (
            <span className="mt-2 flex items-center gap-1">
              {s.activeActors.slice(0, 5).map((a) => (
                <span key={a.id} title={`${a.name}${a.activity ? ` · ${a.activity}` : ""}`} className="inline-flex">
                  <Avatar name={a.name} kind={a.kind} size={20} />
                </span>
              ))}
              {s.activeActors.length > 5 && <span className="ml-1 text-[11.5px] text-ink-3">+{s.activeActors.length - 5}</span>}
            </span>
          )}
        </Metric>
        <Metric label="Open decisions" testId="project-decisions" dataOpen={s.openDecisions} value={String(s.openDecisions)} accent={s.openDecisions > 0} />
        <Metric label="Spent" testId="project-spend" value={spend.totalUsd === null ? "…" : usd(spend.totalUsd)} title="API-equivalent cost of this project's agent runs" />
        <Metric label="Last 24 hours" value={spend.totalUsd === null ? "…" : usd(spend.dayUsd)} />
      </span>
    </button>
  );
}

function Metric({ label, value, testId, dataOpen, accent, title, children }: { label: string; value: string; testId?: string; dataOpen?: number; accent?: boolean; title?: string; children?: ReactNode }) {
  return (
    <span className="rounded-2xl bg-ink/[0.035] px-4 py-3" data-testid={testId} data-open={dataOpen} title={title}>
      <span className="block text-[11.5px] text-ink-3">{label}</span>
      <span className={cx("mt-0.5 block text-[20px] font-medium tabular-nums tracking-[-0.01em]", accent && "text-[color:var(--medium)]")}>{value}</span>
      {children}
    </span>
  );
}

type RepoChoice = "fresh" | "url" | "path";
const REPO_CHOICES: { value: RepoChoice; label: string; placeholder?: string }[] = [
  { value: "fresh", label: "Fresh repo" },
  { value: "url", label: "Git URL", placeholder: "https://github.com/org/repo.git" },
  { value: "path", label: "Existing path", placeholder: "/Users/you/code/repo" },
];

/** Admin-only form: name, description, and how the repo comes to exist. Selects the new project and opens it. */
export function NewProjectDialog({ onClose }: { onClose: () => void }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [mode, setMode] = useState<RepoChoice>("fresh");
  const [value, setValue] = useState("");
  const create = useCreate<ProjectDTO>("/projects", [keys.projects, keys.overview]);
  const { select } = useCurrentProject();
  const navigate = useNavigate();
  const choice = REPO_CHOICES.find((c) => c.value === mode)!;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const repo = mode === "fresh" ? { mode } : mode === "url" ? { mode, url: value.trim() } : { mode, path: value.trim() };
    create.mutate(
      { name: name.trim(), description: description.trim(), repo },
      {
        onSuccess: (project) => {
          onClose();
          select(project.id);
          navigate(`/projects/${project.id}`);
        },
      },
    );
  };
  return (
    <Dialog title="New project" onClose={onClose}>
      <form onSubmit={submit} className="space-y-3" data-testid="new-project-form">
        <Field label="Name">
          <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="new-project-name" />
        </Field>
        <Field label="Description" hint="optional">
          <textarea className={inputCls} rows={2} value={description} onChange={(e) => setDescription(e.target.value)} data-testid="new-project-description" />
        </Field>
        <fieldset>
          <legend className="text-[13px] font-medium text-ink-2">Repository</legend>
          <div className="mt-1.5 space-y-1.5">
            {REPO_CHOICES.map((c) => (
              <label key={c.value} className="flex items-center gap-2 text-[13.5px]">
                <input type="radio" name="repo-mode" checked={mode === c.value} onChange={() => { setMode(c.value); setValue(""); }} data-testid={`new-project-repo-${c.value}`} />
                {c.label}
              </label>
            ))}
          </div>
          {mode !== "fresh" && (
            <input
              className={cx(inputCls, "mt-2 font-mono text-[12.5px]")}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={choice.placeholder}
              aria-label={choice.label}
              data-testid="new-project-repo-value"
            />
          )}
          {mode === "url" && <NewProjectGithub url={value} />}
        </fieldset>
        <ErrorNote error={create.error} />
        <div className="flex justify-end gap-2 pt-1">
          <Action onClick={onClose}>Cancel</Action>
          <Action primary type="submit" disabled={!name.trim() || (mode !== "fresh" && !value.trim()) || create.isPending} testId="new-project-submit">
            Create project
          </Action>
        </div>
      </form>
    </Dialog>
  );
}

const usd = (n: number) => `$${n.toLocaleString("en-US", { minimumFractionDigits: n < 100 ? 2 : 0, maximumFractionDigits: n < 100 ? 2 : 0 })}`;

/** "$1,234 spent · $56 today": total agent-run spend for a project, plus the last 24 hours when there was any. */
export function spendLabel(totalUsd: number, dayUsd: number): string {
  return dayUsd > 0 ? `${usd(totalUsd)} spent · ${usd(dayUsd)} today` : `${usd(totalUsd)} spent`;
}
